import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import type { GetSessionsDto } from '@/types/dtos/sessions/response/get-sessions.dto.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { useMemo } from 'react';

export const GET_EVENT_SESSIONS_QUERY_KEY = 'get-event-sessions';
const PAGE_SIZE = '100';

export const normalizeEventSessionsData = (
  data?: InfiniteData<
    GetSessionsDto | GetSessionsDto[] | [GetSessionsDto | GetSessionsDto[], GetSessionsDto | GetSessionsDto[]]
  >,
): {
  planSessions: SessionDto[];
  organizeSessions: SessionDto[];
  preferredSessions: SessionDto[];
} => {
  const planSessions: SessionDto[] = [];
  const organizeSessions: SessionDto[] = [];

  const appendSessions = (sessions: SessionDto[]) => {
    if (!sessions.length) return;
    const mode = sessions[0]?.mode;
    if (mode === 'PLAN') {
      planSessions.push(...sessions);
      return;
    }
    if (mode === 'ORGANIZE') {
      organizeSessions.push(...sessions);
    }
  };

  data?.pages.forEach((page) => {
    if (Array.isArray(page)) {
      page.forEach((entry) => {
        if (Array.isArray(entry)) {
          entry.forEach((item) => appendSessions(item.data ?? []));
        } else {
          appendSessions(entry.data ?? []);
        }
      });
      return;
    }
    appendSessions(page.data ?? []);
  });

  const preferredSessions = planSessions.length > 0 ? planSessions : organizeSessions;
  return { planSessions, organizeSessions, preferredSessions };
};

// Query event sessions by originId - backend decides what mode documents to return
export const useEventSessionsQuery = ({
  eventId,
  isInLibrary,
  loadAllModes = false,
  enabled = true,
}: {
  eventId?: string;
  isInLibrary?: boolean;
  loadAllModes?: boolean;
  enabled?: boolean;
}) => {
  const { getToken } = useAuth();

  const query = useInfiniteQuery({
    queryKey: [GET_EVENT_SESSIONS_QUERY_KEY, eventId, loadAllModes, isInLibrary ?? null],
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      if (loadAllModes) {
        return Promise.all([
          getEventSessions({ eventId: eventId!, isInLibrary, pageParam, token: token ?? '', mode: 'PLAN' }),
          getEventSessions({ eventId: eventId!, isInLibrary, pageParam, token: token ?? '', mode: 'ORGANIZE' }),
        ]);
      }
      return getEventSessions({ eventId: eventId!, isInLibrary, pageParam, token: token ?? '' });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (loadAllModes) return undefined;
      return Array.isArray(lastPage) ? undefined : lastPage.pagination.nextCursor;
    },
    enabled: !!eventId && enabled,
  });

  const normalized = useMemo(() => normalizeEventSessionsData(query.data), [query.data]);

  return {
    ...query,
    normalized,
  };
};

const getEventSessions = async ({
  eventId,
  isInLibrary,
  pageParam,
  token,
  mode,
}: {
  eventId: string;
  isInLibrary?: boolean;
  pageParam: string;
  token: string;
  mode?: 'PLAN' | 'ORGANIZE';
}): Promise<GetSessionsDto | GetSessionsDto[]> => {
  const searchParams = new URLSearchParams({ limit: PAGE_SIZE });
  if (pageParam !== '') {
    searchParams.append('cursor', pageParam);
  }
  if (isInLibrary) {
    searchParams.append('isInLibrary', isInLibrary.toString());
  }
  if (mode) {
    searchParams.append('mode', mode);
  }
  // Query with originId only - backend provides appropriate document(s) based on access
  const url = `${import.meta.env.VITE_API_URL}/sessions/event/${eventId}?${searchParams.toString()}`;
  return sendJSONRequest({ url, method: METHOD.GET, token });
};
